import { BridgeComponent } from '@hotwired/hotwire-native-bridge'
import { submitForm } from '../../utils/bridge_form'
import { withIconSvg } from '../../utils/bridge_icons'

// Bridge component feeding the native bottom toolbar on a list's show (iOS).
//
// Sends the contextual actions (already policy-filtered server side) to the paired
// ListActionsComponent, which renders a UIToolbar: secondary actions grouped left,
// the prominent « Ajouter » pushed right. Replies come back as
// { action: "select", index } (perform that item) or { action: "reorder" }. Each action
// carries `svg` (and `activeSvg` for the reorder toggle) on Android, see utils/bridge_icons.
//
// The reorder toggle is delegated to the hidden web Organiser/Terminer buttons
// ([data-list-actions-organize] / [data-list-actions-done]) so their whole behaviour
// (active-sort navigation fallback, organize param stripping) stays in one place.
// The effective sortable state is reported back through the `reorderState` event —
// including after a reconnect mid-organize (?organize=true reload rebuilds the
// toolbar in idle state) — so the native toggle reflects reality, not intent.
//
// That same event carries the SELECTION state while organizing: the native
// prominent capsule stops being « Ajouter une envie » and becomes the grouping
// action (disabled until something is ticked), rather than the web stacking a
// second floating bar under the native toolbar. Its tap replies
// { action: "group" }, delegated to the hidden [data-list-actions-group] button.
export default class extends BridgeComponent {
  static component = 'list-actions'
  static values = { actions: Array }

  #notifyReorderState = () => {
    // Only for the page this controller belongs to (see menu_controller).
    if (window.location.pathname !== this.pagePath) return
    this.send('reorderState', { reordering: this.#reordering, grouping: this.#grouping })
  }

  connect() {
    super.connect()
    this.pagePath = window.location.pathname

    window.addEventListener('sortable:activated', this.#notifyReorderState)
    window.addEventListener('sortable:deactivated', this.#notifyReorderState)
    window.addEventListener('selection:changed', this.#notifyReorderState)

    const actions = this.actionsValue.map(withIconSvg)
    this.send('connect', { actions }, (message) => {
      const { action, index } = message.data
      if (action === 'reorder') this.#toggleReorder()
      else if (action === 'group') this.#openGrouping()
      else this.#perform(this.actionsValue[index])
    })

    // sortable may connect (and auto-activate) after us: report the settled state.
    requestAnimationFrame(this.#notifyReorderState)
  }

  disconnect() {
    window.removeEventListener('sortable:activated', this.#notifyReorderState)
    window.removeEventListener('sortable:deactivated', this.#notifyReorderState)
    window.removeEventListener('selection:changed', this.#notifyReorderState)
    super.disconnect()
  }

  #toggleReorder() {
    const selector = this.#reordering ? '[data-list-actions-done]' : '[data-list-actions-organize]'
    document.querySelector(selector)?.click()
  }

  #openGrouping() {
    document.querySelector('[data-list-actions-group]')?.click()
  }

  get #reordering() {
    return !!document.querySelector('[data-list-actions-done][data-sortable]')
  }

  // Apparence de la capsule primaire pendant l'organisation : le libellé vient du
  // bouton caché (i18n côté Rails), le compte est relu dans le DOM plutôt que
  // suivi ici — l'état reste juste même si un évènement est manqué. `null` hors
  // mode organisation : la capsule redevient « Ajouter une envie ».
  get #grouping() {
    const button = document.querySelector('[data-list-actions-group]')
    if (!button || !this.#reordering) return null

    // Capsule en texte seul : une icône y doublerait le libellé, et l'ajouter à
    // une capsule .prominent la placerait de force AVANT le texte.
    const count = document.querySelectorAll('#items-load [data-selected]').length
    const title = button.dataset.groupTitle
    return { title: count > 0 ? `${title} (${count})` : title, enabled: count > 0 }
  }

  #perform(item) {
    if (!item) return

    const method = (item.method || 'get').toLowerCase()
    if (method === 'share' || method === 'reorder') return // handled natively / above
    if (method === 'get') {
      window.Turbo.visit(item.url)
    } else {
      submitForm(item.url, method)
    }
  }
}
